/**
 * Analytics Service for tracking user journey and verification events
 *
 * This service handles comprehensive event tracking throughout the verification flow,
 * providing detailed analytics for user behavior, system performance, and business metrics.
 * Events are batched and sent to the portal-api analytics endpoint to minimize interference
 * with real-time video capture operations.
 *
 * @author SafePassage Engineering
 * @version 1.0.0
 */

import { runtimeConfig } from './RuntimeConfigService';
import { getDevBrandSlug } from '../contexts/DevBrandContext';
import { resolveFlowMode } from '../utils/newTabHandler';
import { sanitizeBrowserMetadata } from '../utils/browserSafety';
import { getCameraPlatformTelemetry } from '../utils/cameraPlatform';

/**
 * Enumeration of all trackable verification journey events
 *
 * These events represent key milestones and user interactions throughout
 * the verification process, enabling comprehensive funnel analysis and
 * performance monitoring.
 */
export enum JourneyEventType {
  // Page/Screen events
  VERIFICATION_PAGE_VIEWED = 'VERIFICATION_PAGE_VIEWED',
  VERIFICATION_PAGE_EXITED = 'VERIFICATION_PAGE_EXITED',

  // User interaction events
  CAMERA_PERMISSION_REQUESTED = 'CAMERA_PERMISSION_REQUESTED',
  CAMERA_PERMISSION_GRANTED = 'CAMERA_PERMISSION_GRANTED',
  CAMERA_PERMISSION_DENIED = 'CAMERA_PERMISSION_DENIED',

  SELFIE_CAPTURE_STARTED = 'SELFIE_CAPTURE_STARTED',
  SELFIE_CAPTURE_COMPLETED = 'SELFIE_CAPTURE_COMPLETED',
  SELFIE_CAPTURE_FAILED = 'SELFIE_CAPTURE_FAILED',
  SELFIE_CAPTURE_RETRIED = 'SELFIE_CAPTURE_RETRIED',

  ID_CAPTURE_STARTED = 'ID_CAPTURE_STARTED',
  ID_CAPTURE_COMPLETED = 'ID_CAPTURE_COMPLETED',
  ID_CAPTURE_FAILED = 'ID_CAPTURE_FAILED',
  ID_CAPTURE_RETRIED = 'ID_CAPTURE_RETRIED',

  // Identity verification events
  ID_VERIFICATION_STARTED = 'ID_VERIFICATION_STARTED',
  ID_VERIFICATION_STEP = 'ID_VERIFICATION_STEP',
  ID_VERIFICATION_COMPLETED = 'ID_VERIFICATION_COMPLETED',
  ID_VERIFICATION_ERROR = 'ID_VERIFICATION_ERROR',

  // Flow events
  VERIFICATION_FLOW_STARTED = 'VERIFICATION_FLOW_STARTED',
  VERIFICATION_FLOW_ABANDONED = 'VERIFICATION_FLOW_ABANDONED',
  VERIFICATION_FLOW_COMPLETED = 'VERIFICATION_FLOW_COMPLETED',

  // Error events
  BROWSER_NOT_SUPPORTED = 'BROWSER_NOT_SUPPORTED',
  CAMERA_NOT_AVAILABLE = 'CAMERA_NOT_AVAILABLE',
  NETWORK_ERROR = 'NETWORK_ERROR',
  SESSION_EXPIRED = 'SESSION_EXPIRED',
}

export type DocumentCameraSetupStatus =
  | 'started'
  | 'ready'
  | 'failed'
  | 'timeout'
  | 'fallback';

interface DocumentCameraSetupMetadata {
  constraintProfile?: string;
  elapsedMs?: number;
  deviceType?: 'mobile' | 'desktop';
  facingMode?: 'environment' | 'user' | 'generic';
  errorName?: string;
  errorCategory?: string;
  fallbackFrom?: string;
  fallbackTo?: string;
  isIOSSafari?: boolean;
}

/**
 * Selfie-camera initialization stages, from the first getUserMedia request
 * through usable video, plus the classified ways that sequence can end.
 */
export type SelfieCameraSetupStatus =
  | 'started'
  | 'stream_acquired'
  | 'ready'
  | 'stalled'
  | 'interrupted'
  | 'failed'
  | 'timeout';

/**
 * Privacy-safe camera diagnostics: timings, dimensions, negotiation labels,
 * and classifications only. Never imagery, biometrics, or device labels.
 * Key names deliberately avoid fragments the browser sanitizer strips.
 */
export interface SelfieCameraSetupMetadata {
  elapsedMs?: number;
  /** Prerequisite work (Portal lifecycle alignment) before getUserMedia. */
  prerequisiteElapsedMs?: number;
  /** Portal round trips that settled during the prerequisite window. */
  prerequisiteRequestCount?: number;
  /** Ordered labels of those round trips, e.g. `start_attempt>advance:face_capture`. */
  prerequisiteTrail?: string;
  /** Gap between prerequisite completion and the getUserMedia call. */
  prerequisiteToRequestMs?: number;
  /** Distance of this stage from the moment the capture screen was shown. */
  screenElapsedMs?: number;
  streamElapsedMs?: number;
  elementWaitMs?: number;
  metadataElapsedMs?: number;
  usableElapsedMs?: number;
  constraintProfile?: string;
  fallbackLevel?: number;
  totalProfiles?: number;
  videoWidth?: number;
  videoHeight?: number;
  readySignal?: string;
  errorName?: string;
  errorType?: string;
  errorCategory?: string;
  stallReason?: string;
  videoReadyState?: number;
  videoPaused?: boolean;
  trackReadyState?: string;
  trackMuted?: boolean;
  trackMutedAtAcquire?: boolean;
  lifecycleTrail?: string;
  playRejectedName?: string;
  retryNumber?: number;
  facingMode?: string;
  quality?: string;
}

export type SelfieHandoffTelemetryStatus =
  | 'interrupted'
  | 'retrying'
  | 'recovered'
  | 'retry_exhausted';

export type SelfieHandoffTelemetrySource = 'websocket' | 'artifact_read';

interface SelfieHandoffTelemetryMetadata {
  source: SelfieHandoffTelemetrySource;
  reason?: string;
  retryNumber?: number;
}

/**
 * Structure representing a single analytics event
 *
 * @interface AnalyticsEvent
 * @property {string} sessionId - Unique identifier for the verification session
 * @property {JourneyEventType} eventType - Type of event being tracked
 * @property {Date} timestamp - When the event occurred
 * @property {Record<string, unknown>} [metadata] - Additional event-specific data
 */
interface AnalyticsEvent {
  sessionId: string;
  eventType: JourneyEventType;
  timestamp: Date;
  metadata?: Record<string, unknown>;
}

/**
 * Analytics Service Class
 *
 * Manages comprehensive event tracking for the verification flow with features:
 * - Batched event transmission to minimize video capture interference
 * - Device information collection for analytics insights
 * - Page/screen navigation tracking with time-on-page metrics
 * - Automatic session lifecycle management
 * - Error and performance monitoring
 *
 * The service uses a 10-second batch interval (increased from default) to ensure
 * video capture operations are not interrupted by network requests.
 */
class AnalyticsService {
  private sessionId: string | null = null;
  private apiKey: string | null = null;
  private eventQueue: AnalyticsEvent[] = [];
  private batchTimer: NodeJS.Timeout | null = null;
  private pageEntryTime: number | null = null;
  private currentScreen: string | null = null;
  private readonly BATCH_INTERVAL = 10000; // 10 seconds - increased to reduce interference with video capture
  private readonly MAX_BATCH_SIZE = 50;
  private readonly FLOW_MODE_EVENTS = new Set<JourneyEventType>([
    JourneyEventType.VERIFICATION_FLOW_STARTED,
    JourneyEventType.VERIFICATION_FLOW_COMPLETED,
    JourneyEventType.VERIFICATION_FLOW_ABANDONED,
  ]);

  private deviceInfo = {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  /**
   * Initialize the analytics service for a verification session
   *
   * Sets up event tracking, device information collection, and automatic
   * cleanup handlers. Immediately sends a VERIFICATION_FLOW_STARTED event
   * with comprehensive device and session metadata.
   *
   * @param {string} sessionId - Unique session identifier from portal-api
   * @param {string} apiKey - API key for authentication with analytics endpoint
   * @throws {Error} If sessionId or apiKey is invalid
   */
  initialize(sessionId: string, apiKey: string) {
    console.log('[SafePassage] [Analytics] Initializing analytics service', {
      sessionId,
      apiKey: '[HIDDEN]',
    });

    this.sessionId = sessionId;
    this.apiKey = apiKey;

    // Start batch timer
    this.startBatchTimer();

    // Send flow started event
    this.trackEvent(JourneyEventType.VERIFICATION_FLOW_STARTED, {
      ...this.deviceInfo,
      referrer: document.referrer || 'direct',
      path: window.location.pathname,
    });

    console.log('[SafePassage] [Analytics] Flow started event tracked', {
      sessionId,
      deviceInfo: this.deviceInfo,
    });

    // Listen for page unload to send remaining events
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  /**
   * Track page/screen navigation with time-on-page calculation
   *
   * Automatically calculates time spent on previous screen and tracks
   * both exit and entry events for comprehensive funnel analysis.
   *
   * @param {string} screenId - Unique identifier for the screen
   * @param {string} screenName - Human-readable screen name
   */
  trackPageView(screenId: string, screenName: string) {
    const now = Date.now();

    // If there was a previous screen, track exit and calculate time spent
    if (this.currentScreen && this.pageEntryTime) {
      const timeOnScreen = now - this.pageEntryTime;
      this.trackEvent(JourneyEventType.VERIFICATION_PAGE_EXITED, {
        screenId: this.currentScreen,
        timeOnScreen,
        nextScreen: screenId,
      });
    }

    // Track new page view
    this.trackEvent(JourneyEventType.VERIFICATION_PAGE_VIEWED, {
      screenId,
      screenName,
      previousScreen: this.currentScreen,
    });

    // Update state
    this.currentScreen = screenId;
    this.pageEntryTime = now;
  }

  /**
   * Track a verification journey event with optional metadata
   *
   * Events are queued for batch transmission to minimize interference
   * with real-time video operations. Automatic batching occurs when
   * queue reaches maximum size or on timer interval.
   *
   * @param {JourneyEventType} eventType - Type of event to track
   * @param {Record<string, unknown>} [metadata] - Additional event data
   */
  trackEvent(eventType: JourneyEventType, metadata?: Record<string, unknown>) {
    if (!this.sessionId) {
      console.warn('Analytics not initialized, skipping event:', eventType);
      return;
    }

    const flowMode = this.getFlowMode();
    const hasFlowMode =
      metadata && Object.prototype.hasOwnProperty.call(metadata, 'flowMode');
    const safeMetadata = sanitizeBrowserMetadata(metadata);
    const event: AnalyticsEvent = {
      sessionId: this.sessionId,
      eventType,
      timestamp: new Date(),
      metadata: {
        ...safeMetadata,
        clientTimestamp: Date.now(),
        ...(flowMode &&
          this.FLOW_MODE_EVENTS.has(eventType) &&
          !hasFlowMode && { flowMode }),
      },
    };

    this.eventQueue.push(event);

    // Send batch if queue is getting large
    if (this.eventQueue.length >= this.MAX_BATCH_SIZE) {
      this.sendBatch();
    }
  }

  /**
   * Track camera permission request outcome
   *
   * @param {boolean} granted - Whether camera permission was granted
   */
  trackCameraPermission(
    granted: boolean,
    metadata: Record<string, unknown> = {}
  ) {
    this.trackEvent(
      granted
        ? JourneyEventType.CAMERA_PERMISSION_GRANTED
        : JourneyEventType.CAMERA_PERMISSION_DENIED,
      { ...getCameraPlatformTelemetry(), ...metadata, granted }
    );
  }

  /**
   * Track selfie-camera initialization stages without introducing new backend
   * enum values. Existing camera event types carry cameraPhase/setupStatus
   * markers (mirroring the document-camera markers) so each stage, stall,
   * and interruption is queryable per session and device family.
   */
  trackSelfieCameraSetup(
    status: SelfieCameraSetupStatus,
    metadata: SelfieCameraSetupMetadata = {}
  ) {
    const eventMap: Record<SelfieCameraSetupStatus, JourneyEventType> = {
      started: JourneyEventType.CAMERA_PERMISSION_REQUESTED,
      stream_acquired: JourneyEventType.CAMERA_PERMISSION_GRANTED,
      ready: JourneyEventType.CAMERA_PERMISSION_GRANTED,
      stalled: JourneyEventType.CAMERA_NOT_AVAILABLE,
      interrupted: JourneyEventType.CAMERA_NOT_AVAILABLE,
      failed: JourneyEventType.CAMERA_NOT_AVAILABLE,
      timeout: JourneyEventType.CAMERA_NOT_AVAILABLE,
    };

    this.trackEvent(eventMap[status], {
      ...this.getRuntimeAnalyticsContext(),
      ...getCameraPlatformTelemetry(),
      ...metadata,
      // Preserve the historical permission-granted payload shape for the
      // first successful acquisition of the session.
      ...(status === 'stream_acquired' ? { granted: true } : {}),
      cameraPhase: 'selfie_capture',
      setupStatus: status,
    });
  }

  /**
   * Track selfie capture events throughout the capture process
   *
   * @param {'started' | 'completed' | 'failed' | 'retried'} status - Capture status
   * @param {Record<string, unknown>} [metadata] - Additional capture data (quality, attempts, etc.)
   */
  trackSelfieCapture(
    status: 'started' | 'completed' | 'failed' | 'retried',
    metadata?: Record<string, unknown>
  ) {
    const eventMap = {
      started: JourneyEventType.SELFIE_CAPTURE_STARTED,
      completed: JourneyEventType.SELFIE_CAPTURE_COMPLETED,
      failed: JourneyEventType.SELFIE_CAPTURE_FAILED,
      retried: JourneyEventType.SELFIE_CAPTURE_RETRIED,
    };

    this.trackEvent(eventMap[status], metadata);
  }

  /**
   * Track ID document capture events
   *
   * @param {'started' | 'completed' | 'failed' | 'retried'} status - Capture status
   * @param {Record<string, unknown>} [metadata] - Document capture metadata
   */
  trackIdCapture(
    status: 'started' | 'completed' | 'failed' | 'retried',
    metadata?: Record<string, unknown>
  ) {
    const eventMap = {
      started: JourneyEventType.ID_CAPTURE_STARTED,
      completed: JourneyEventType.ID_CAPTURE_COMPLETED,
      failed: JourneyEventType.ID_CAPTURE_FAILED,
      retried: JourneyEventType.ID_CAPTURE_RETRIED,
    };

    this.trackEvent(eventMap[status], metadata);
  }

  /**
   * Track document-camera setup without introducing new backend enum values.
   * Existing event types keep this deployable as a verify-ui-only hotfix while
   * cameraPhase/setupStatus make the markers queryable.
   */
  trackDocumentCameraSetup(
    status: DocumentCameraSetupStatus,
    metadata: DocumentCameraSetupMetadata = {}
  ) {
    const eventMap: Record<DocumentCameraSetupStatus, JourneyEventType> = {
      started: JourneyEventType.CAMERA_PERMISSION_REQUESTED,
      ready: JourneyEventType.CAMERA_PERMISSION_GRANTED,
      failed: JourneyEventType.CAMERA_NOT_AVAILABLE,
      timeout: JourneyEventType.CAMERA_NOT_AVAILABLE,
      fallback: JourneyEventType.CAMERA_PERMISSION_REQUESTED,
    };

    this.trackEvent(eventMap[status], {
      ...this.getRuntimeAnalyticsContext(),
      ...metadata,
      cameraPhase: 'document_capture',
      setupStatus: status,
    });
  }

  /**
   * Record the non-terminal product interruption independently of React error
   * boundaries. NETWORK_ERROR is the existing persisted event category; the
   * structured fields preserve the pipeline meaning without schema changes.
   */
  trackSelfieHandoff(
    status: SelfieHandoffTelemetryStatus,
    metadata: SelfieHandoffTelemetryMetadata
  ) {
    this.trackEvent(JourneyEventType.NETWORK_ERROR, {
      ...this.getRuntimeAnalyticsContext(),
      pipelineEvent: 'selfie_handoff',
      failureStage: 'selfie_handoff',
      failureCategory: metadata.reason || 'temporarily_unavailable',
      stageStatus: status,
      interruptionSource: metadata.source,
      retryNumber: metadata.retryNumber,
      retryable: true,
      terminal: false,
      code: 'SELFIE_HANDOFF_UNAVAILABLE',
    });
  }

  /**
   * Track verification flow abandonment
   *
   * Automatically calculates final screen time and immediately flushes
   * all pending events to ensure abandonment data is captured.
   *
   * @param {string} [reason] - Optional reason for abandonment
   */
  trackFlowAbandoned(reason?: string) {
    // Track final page exit if needed
    if (this.currentScreen && this.pageEntryTime) {
      const timeOnScreen = Date.now() - this.pageEntryTime;
      this.trackEvent(JourneyEventType.VERIFICATION_PAGE_EXITED, {
        screenId: this.currentScreen,
        timeOnScreen,
        abandoned: true,
      });
    }

    this.trackEvent(JourneyEventType.VERIFICATION_FLOW_ABANDONED, {
      lastScreen: this.currentScreen,
      reason,
    });

    // Flush events immediately
    this.flush();
  }

  /**
   * Track verification flow completion
   *
   * Marks the end of the verification journey and immediately flushes
   * all events to ensure completion data is captured.
   *
   * @param {boolean} success - Whether verification was successful
   */
  trackFlowCompleted(success: boolean) {
    this.trackEvent(JourneyEventType.VERIFICATION_FLOW_COMPLETED, {
      success,
      lastScreen: this.currentScreen,
    });

    // Flush events immediately
    this.flush();
  }

  /**
   * Track system errors during verification
   *
   * @param {'browser' | 'camera' | 'network' | 'session'} errorType - Category of error
   * @param {Record<string, unknown>} [details] - Error details and context
   */
  trackError(
    errorType: 'browser' | 'camera' | 'network' | 'session',
    details?: Record<string, unknown>
  ) {
    const eventMap = {
      browser: JourneyEventType.BROWSER_NOT_SUPPORTED,
      camera: JourneyEventType.CAMERA_NOT_AVAILABLE,
      network: JourneyEventType.NETWORK_ERROR,
      session: JourneyEventType.SESSION_EXPIRED,
    };

    this.trackEvent(eventMap[errorType], details);
  }

  /**
   * Start the batch timer for periodic event transmission
   *
   * Uses 10-second intervals (increased from default) to minimize
   * interference with video capture operations.
   *
   * @private
   */
  private startBatchTimer() {
    this.batchTimer = setInterval(() => {
      if (this.eventQueue.length > 0) {
        this.sendBatch();
      }
    }, this.BATCH_INTERVAL);
  }

  /**
   * Send batched events to analytics endpoint
   *
   * Critical implementation detail: Uses setTimeout to prevent blocking
   * the main thread during video capture. Synchronous network operations
   * can interfere with canvas.drawImage() and canvas.toDataURL() causing
   * corrupted frames and failed feature detection.
   *
   * @private
   */
  private async sendBatch() {
    if (this.eventQueue.length === 0 || !this.apiKey) {
      return;
    }

    // Take current batch
    const batch = [...this.eventQueue];
    this.eventQueue = [];

    // IMPORTANT: Use setTimeout to prevent blocking the main thread during video capture
    // Without this, synchronous network operations interfere with canvas.drawImage()
    // and canvas.toDataURL() causing corrupted frames and failed feature detection
    setTimeout(async () => {
      try {
        // Phase 2: Get config from URL state instead of fetching from portal-api
        const config = runtimeConfig.getConfig();
        const portalApiUrl = config.apiUrl;

        // Phase 2: Use API key for authentication instead of internal token
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey || '', // Use API key from state parameter
          'X-Session-ID': this.sessionId || '', // Include session ID for correlation
        };

        // Add brand override header for localhost development
        const devBrand = getDevBrandSlug();
        if (devBrand) {
          headers['X-Brand-Override'] = devBrand;
        }

        // Phase 2: Send to public analytics endpoint instead of internal endpoint
        const response = await fetch(
          `${portalApiUrl}/api/v1/analytics/events`,
          {
            method: 'POST',
            headers,
            body: JSON.stringify({
              events: batch.map((event) => ({
                eventType: event.eventType,
                sessionId: event.sessionId,
                metadata: event.metadata,
                timestamp:
                  event.timestamp instanceof Date
                    ? event.timestamp.toISOString()
                    : event.timestamp,
              })),
            }),
          }
        );

        if (!response.ok) {
          console.error('Failed to send analytics batch:', response.status);
          // If unauthorized, disable further analytics to reduce noise during troubleshooting
          if (response.status === 401) {
            console.warn('[Analytics] Disabling analytics due to 401 (invalid API key)');
            this.apiKey = null;
            return; // Drop this batch silently
          }
          // Re-queue events on other failures (with limit to prevent memory issues)
          if (this.eventQueue.length < this.MAX_BATCH_SIZE * 2) {
            this.eventQueue.unshift(...batch);
          }
        }
      } catch (error) {
        console.error('Error sending analytics batch:', error);
        // Re-queue events on network error
        if (this.eventQueue.length < this.MAX_BATCH_SIZE * 2) {
          this.eventQueue.unshift(...batch);
        }
      }
    }, 0);
  }

  /**
   * Immediately flush all pending events
   *
   * Called during flow abandonment, completion, or page unload
   * to ensure no events are lost.
   */
  flush() {
    if (this.batchTimer) {
      clearInterval(this.batchTimer);
      this.batchTimer = null;
    }

    // Send any remaining events
    this.sendBatch();
  }

  /**
   * Clean up analytics service resources
   *
   * Flushes remaining events and resets all internal state.
   * Should be called when verification session ends.
   */
  destroy() {
    this.flush();
    this.sessionId = null;
    this.apiKey = null;
    this.eventQueue = [];
    this.currentScreen = null;
    this.pageEntryTime = null;
  }

  private getFlowMode(): string | null {
    try {
      return resolveFlowMode();
    } catch {
      return null;
    }
  }

  private getRuntimeAnalyticsContext(): Record<string, string> {
    const context: Record<string, string> = {};

    try {
      const config = runtimeConfig.getConfig();
      if (config.brand?.brandSlug) {
        context.brandSlug = config.brand.brandSlug;
      }
      if (config.verificationMode) {
        context.verificationMode = config.verificationMode;
      }
    } catch {
      // Runtime config may not be ready during early camera recovery.
    }

    const flowMode = this.getFlowMode();
    if (flowMode) {
      context.flowMode = flowMode;
    }

    return context;
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();
